import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#por-que-ia", key: "navbar.por_que_ia" },
    { href: "#servicios", key: "navbar.servicios_agentes" },
    { href: "#roi", key: "navbar.impacto_roi" },
    { href: "#como-trabajamos", key: "navbar.como_trabajamos" },
    { href: "#diagnostico", key: "navbar.diagnostico_roi" },
  ];

  const currentLang = i18n.language?.startsWith("en") ? "en" : "es";

  const switchLang = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  const scrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileOpen(false);
  };

  const openDemo = () => {
    window.open("https://calendly.com/alex-alcalde-aidealmedia-pyr_/30min", "_blank", "noopener,noreferrer");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/85 backdrop-blur-xl border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <nav className="w-full px-4 md:px-10 lg:px-16 py-4">
        <div className="flex items-center justify-between h-[72px]">
          {/* Logo */}
          <a
            href="#top"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center cursor-pointer"
          >
            <img
              src="/img/logo-imagotipo-positivo.webp"
              alt="AIdeal Media"
              className="h-[68px] w-auto object-contain"
              style={{ background: "transparent" }}
            />
          </a>

          {/* Desktop nav */}
          <ul className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollTo(l.href);
                  }}
                  className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${scrolled ? "text-foreground-800 hover:text-primary-600" : "text-white hover:text-accent-400"}`}
                >
                  {t(l.key)}
                </a>
              </li>
            ))}
          </ul>

          {/* CTAs */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Language switcher */}
            <div className="flex items-center gap-1 text-sm font-semibold">
              <button
                onClick={() => switchLang("es")}
                className={`relative px-1.5 py-0.5 cursor-pointer whitespace-nowrap transition-all duration-300 ${
                  currentLang === "es"
                    ? scrolled
                      ? "text-foreground-900 scale-105"
                      : "text-white scale-105"
                    : scrolled
                      ? "text-foreground-400 hover:text-foreground-600"
                      : "text-white/40 hover:text-white/70"
                }`}
                aria-label="Español"
              >
                ES
              </button>
              <span className={`${scrolled ? "text-foreground-300" : "text-white/30"}`}>|</span>
              <button
                onClick={() => switchLang("en")}
                className={`relative px-1.5 py-0.5 cursor-pointer whitespace-nowrap transition-all duration-300 ${
                  currentLang === "en"
                    ? scrolled
                      ? "text-foreground-900 scale-105"
                      : "text-white scale-105"
                    : scrolled
                      ? "text-foreground-400 hover:text-foreground-600"
                      : "text-white/40 hover:text-white/70"
                }`}
                aria-label="English"
              >
                EN
              </button>
            </div>

            <button
              onClick={openDemo}
              className="px-5 py-2.5 rounded-md text-sm font-semibold text-background-50 brand-gradient-btn cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              {t("navbar.reservar_consulta")}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </button>
          </div>

          {/* Mobile lang switcher + toggle */}
          <div className="flex lg:hidden items-center gap-3">
            {/* Language switcher — always visible, left of hamburger */}
            <div className="flex items-center gap-1 text-xs font-semibold">
              <button
                onClick={() => switchLang("es")}
                className={`relative px-1.5 py-0.5 cursor-pointer whitespace-nowrap transition-all duration-300 ${
                  currentLang === "es"
                    ? scrolled
                      ? "text-foreground-900 scale-105"
                      : "text-white scale-105"
                    : scrolled
                      ? "text-foreground-400 hover:text-foreground-600"
                      : "text-white/40 hover:text-white/70"
                }`}
                aria-label="Español"
              >
                ES
              </button>
              <span className={`${scrolled ? "text-foreground-300" : "text-white/30"}`}>|</span>
              <button
                onClick={() => switchLang("en")}
                className={`relative px-1.5 py-0.5 cursor-pointer whitespace-nowrap transition-all duration-300 ${
                  currentLang === "en"
                    ? scrolled
                      ? "text-foreground-900 scale-105"
                      : "text-white scale-105"
                    : scrolled
                      ? "text-foreground-400 hover:text-foreground-600"
                      : "text-white/40 hover:text-white/70"
                }`}
                aria-label="English"
              >
                EN
              </button>
            </div>

            {/* Hamburger */}
            <button
              className={`w-10 h-10 flex items-center justify-center rounded-md cursor-pointer transition-colors ${scrolled ? "text-foreground-800 hover:bg-background-100" : "text-white hover:bg-white/10"}`}
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              <span className="w-6 h-6 flex items-center justify-center">
                <i className={mobileOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"}></i>
              </span>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden pb-5 pt-2 border-t border-background-200/70 bg-background-50/95 backdrop-blur-xl">
            <ul className="flex flex-col gap-1 pt-3">
              {links.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollTo(l.href);
                    }}
                    className="block px-3 py-2.5 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer"
                  >
                    {t(l.key)}
                  </a>
                </li>
              ))}
            </ul>
            <div className="flex flex-col gap-2 mt-4">
              <button
                onClick={openDemo}
                className="w-full px-4 py-2.5 rounded-md text-sm font-semibold text-background-50 brand-gradient-btn cursor-pointer whitespace-nowrap"
              >
                {t("navbar.reservar_consulta")}
              </button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}